import React, { useEffect, useRef, useState } from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { withTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { LogoutUser } from "../../Apis/loginApi";
import isGHMC from "../../Common/helper/helpar";
import { InlineLoader } from "../ShimmerLoader";
import { SidebarMenuItems } from "./SidebarMenuItems";
import {
  SECTION_PARENT_IDS,
  TOP_LEVEL_SECTION_IDS,
  createInitialOpenSections,
  createSidebarPermissionCheckers,
  readSidebarPermissions,
  sidebarMenuItems,
} from "./sidebarMenuConfig";
import renvionLogo from "../../../assets/images/renvion-logo-solid.svg";
import "./sidebar.css";

const Sidebar = ({ t, isSidebarVisible = true, onCloseSidebar }) => {
  const navigationRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();
  const isProd = isGHMC();
  const menuIconClass = isProd ? "icon-blue" : "icon-red";

  const [prodClassName, setProdClassName] = useState("");
  const [openSections, setOpenSections] = useState(() =>
    createInitialOpenSections(location.pathname),
  );
  const [isLoading] = useState(false);
  const [isCompact, setIsCompact] = useState(() => {
    try {
      return localStorage.getItem("sidebarCompact") === "true";
    } catch (error) {
      return false;
    }
  });

  const handleCloseSidebar = () => {
    // Only auto-close after menu click on small screens (overlay mode).
    // Medium/desktop keep the sidebar open.
    if (typeof onCloseSidebar === "function" && window.innerWidth <= 767.98) {
      onCloseSidebar();
    }
  };

  useEffect(() => {
    setProdClassName(isGHMC() ? "ghmc-true" : "ghmc-false");
  }, []);

  useEffect(() => {
    const navigationEl = navigationRef.current;
    if (!navigationEl) {
      return;
    }

    // Timing lives in sidebar.css (shared with the content/header shift)
    navigationEl.style.transform = isSidebarVisible
      ? "translateX(0)"
      : "translateX(-100%)";
  }, [isSidebarVisible]);

  useEffect(() => {
    document.body.classList.toggle("sidebar-compact", isCompact);
    try {
      localStorage.setItem("sidebarCompact", String(isCompact));
    } catch (error) {
      // Storage unavailable — compact state just won't persist.
    }
    return () => document.body.classList.remove("sidebar-compact");
  }, [isCompact]);

  const permissions = createSidebarPermissionCheckers(readSidebarPermissions());
  const userName = localStorage.getItem("userName") || "User";

  const handleToggleSection = (
    sectionId,
    { siblingSectionIds = [], parentKey = "" } = {},
  ) => {
    // In the icon rail a section can't show its children, so expand first.
    if (isCompact) {
      setIsCompact(false);
      if (openSections[sectionId]) {
        return;
      }
    }

    setOpenSections((currentSections) => {
      const parentSectionId = SECTION_PARENT_IDS[sectionId];
      const shouldOpenSection = !currentSections[sectionId];

      if (TOP_LEVEL_SECTION_IDS.includes(sectionId)) {
        const nextSections = Object.keys(currentSections).reduce(
          (sections, key) => ({
            ...sections,
            [key]: false,
          }),
          {},
        );
        nextSections[sectionId] = shouldOpenSection;
        return nextSections;
      }

      if (parentSectionId) {
        const nextSections = Object.keys(currentSections).reduce(
          (sections, key) => ({
            ...sections,
            [key]: false,
          }),
          {},
        );

        let ancestorId = parentSectionId;
        while (ancestorId) {
          nextSections[ancestorId] = true;
          ancestorId = SECTION_PARENT_IDS[ancestorId];
        }

        nextSections[sectionId] = shouldOpenSection;
        return nextSections;
      }

      const nextSections = { ...currentSections };

      siblingSectionIds.forEach((siblingId) => {
        nextSections[siblingId] = false;
      });

      if (parentKey) {
        nextSections[parentKey] = true;
      }

      nextSections[sectionId] = shouldOpenSection;
      return nextSections;
    });
  };

  const handlePermissionSelect = (permissionId) => {
    localStorage.setItem("permissionId", permissionId);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    let decodedToken = null;

    try {
      if (token) {
        decodedToken = jwtDecode(token);
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      toast.error("There was an issue with your session. Please log in again.");
      return;
    }

    const payload = {
      token,
      userId: decodedToken?.UserId || null,
      sessionId: decodedToken?.SessionId || null,
      userType: decodedToken?.UserType || null,
    };

    try {
      const response = await LogoutUser(payload);

      if (response.statusCode === 404) {
        toast.error("Your session has already expired or is invalid.");
      } else {
        toast.success("You have been successfully logged out.");
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("sessionId");
        localStorage.removeItem("userType");
        localStorage.removeItem("selectedItems");

        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      }
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("An error occurred during logout. Please try again.");
    }
  };

  const handleLogoutClick = () => {
    handleLogout();
    localStorage.clear();
    handleCloseSidebar();
    navigate("/");
  };

  return (
    <div
      className={`app-sidebar sidebar-shadow${isSidebarVisible ? " is-open" : ""}`}
    >
      <button
        type="button"
        className="sidebar-backdrop"
        aria-label="Close sidebar"
        onClick={handleCloseSidebar}
      />
      <div
        ref={navigationRef}
        className={[
          "navigation",
          prodClassName,
          isSidebarVisible ? "open" : "",
          isCompact ? "is-compact" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="sidebar-card">
          <div className="navigation-header">
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={handleCloseSidebar}
              aria-label="Close sidebar"
            >
              <i className="ti-close" />
            </button>
          </div>

          <div className="sidebar-profile">
            <span className="sidebar-brand-mark">
              <img
                src={renvionLogo}
                alt="Renvion"
                className="sidebar-brand-logo"
              />
            </span>
            <div className="sidebar-brand-wrap">
              <span className="sidebar-brand-tag">renvion</span>
              <span className="sidebar-brand-sub">G u r u g r a m</span>
            </div>
            <button
              type="button"
              className="sidebar-compact-toggle"
              onClick={() => setIsCompact((value) => !value)}
              aria-label={isCompact ? "Expand sidebar" : "Collapse sidebar"}
              title={isCompact ? "Expand" : "Collapse"}
            >
              <i
                className={`fa ${isCompact ? "fa-chevron-right" : "fa-chevron-left"}`}
              />
            </button>
          </div>

          <div className="sidebar-section-label">
            <span>Menu</span>
            <i className="fa fa-th-large" aria-hidden="true" />
          </div>

          <div className="navigation-menu-body">
            <List className={classes.appMenu} disablePadding>
              <SidebarMenuItems
                items={sidebarMenuItems}
                t={t}
                classes={classes}
                isProd={isProd}
                location={location}
                menuIconClass={menuIconClass}
                permissions={permissions}
                openSections={openSections}
                onToggleSection={handleToggleSection}
                onCloseSidebar={handleCloseSidebar}
                onPermissionSelect={handlePermissionSelect}
              />
            </List>
            {isLoading && <InlineLoader />}
          </div>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="sidebar-user-info">
                <span className="sidebar-user-name" title={userName}>
                  {userName}
                </span>
                <span className="sidebar-user-role">Signed in as</span>
              </div>
              <button
                type="button"
                className="sidebar-logout-btn"
                onClick={handleLogoutClick}
                title="Logout"
                aria-label="Logout"
              >
                <i className="fa fa-sign-out-alt" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const drawerWidth = "";
const useStyles = makeStyles(() =>
  createStyles({
    appMenu: {
      width: "100%",
    },
    navList: {
      width: drawerWidth,
    },
    menuItem: {
      width: drawerWidth,
    },
    menuItemIcon: {
      minWidth: 32,
    },
    collapseInner: {
      backgroundColor: "#fff5f5",
    },
    nestedList: {
      position: "absolute",
      top: "0",
      left: "100%",
      minWidth: "240px",
      backgroundColor: "#fff",
      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
      zIndex: "999",
    },
  }),
);

export default withTranslation()(Sidebar);
